import React, { useEffect, useState, useCallback } from "react";
import { connect } from "react-redux";
import StockLevelsDashboard from "../../components/marketInventory/StockLevelsDashboard";
import {
  fetchLocationsSuccess,
  fetchLocationsFailure,
  fetchStockLevelsSuccess,
  fetchStockLevelsFailure
} from "../../store/marketInventory/actions";
import * as API from "../../store/marketInventory/api";
import { isStaffUser, isSuperUser } from "../../utils/AuthService";

const StockLevelsDashboardContainer = ({
  stockLevels,
  locations,
  fetchLocationsSuccess,
  fetchLocationsFailure,
  fetchStockLevelsSuccess,
  fetchStockLevelsFailure
}) => {
  const isAdmin = isStaffUser() || isSuperUser();
  const [selectedLocationId, setSelectedLocationId] = useState(isAdmin ? null : "auto");

  // Fetch locations on mount (admin only - market users don't need to pick)
  useEffect(() => {
    if (!isAdmin) return;
    const loadLocations = async () => {
      try {
        const response = await API.fetchLocations();
        fetchLocationsSuccess(response.data);
        // Auto-select first location if available
        const locationsList = Array.isArray(response.data)
          ? response.data
          : (response.data.results || []);
        if (locationsList.length > 0) {
          setSelectedLocationId(locationsList[0].id);
        }
      } catch (error) {
        console.error("Error fetching locations:", error);
        fetchLocationsFailure(error.response?.data?.message || "Failed to fetch locations");
      }
    };
    loadLocations();
  }, [isAdmin, fetchLocationsSuccess, fetchLocationsFailure]);

  const handleFetchStockLevels = useCallback(async (locationId, lowStockOnly) => {
    try {
      // Market users: don't send location_id, backend resolves from user
      const locId = isAdmin ? locationId : undefined;
      const response = await API.fetchStockLevels(locId, lowStockOnly);
      fetchStockLevelsSuccess(response.data);
    } catch (error) {
      console.error("Error fetching stock levels:", error);
      const errorMessage = error.response?.data?.message || "Failed to fetch stock levels";
      fetchStockLevelsFailure(errorMessage);
    }
  }, [isAdmin, fetchStockLevelsSuccess, fetchStockLevelsFailure]);

  const handleLocationChange = (e) => {
    const locationId = e.target.value ? parseInt(e.target.value) : null;
    setSelectedLocationId(locationId);
  };

  // Get selected location name
  const selectedLocationName = Array.isArray(locations.list)
    ? locations.list.find(l => l.id === selectedLocationId)?.name
    : null;

  return (
    <div className="container-fluid">
      {/* Page Header */}
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <span>
            <i className="fa fa-cubes mr-05"></i>
            Stock Levels
          </span>
          {isAdmin && (
            <div className="d-flex align-items-center">
              <label className="mb-0 mr-05" style={{ whiteSpace: 'nowrap' }}>
                <i className="fa fa-map-marker mr-05"></i>
                Location:
              </label>
              <select
                className="form-control"
                style={{ width: '200px' }}
                value={selectedLocationId || ""}
                onChange={handleLocationChange}
                disabled={locations.loading}
              >
                <option value="">Select location...</option>
                {Array.isArray(locations.list) && locations.list.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {selectedLocationId ? (
        <StockLevelsDashboard
          stockLevels={stockLevels}
          loading={stockLevels.loading}
          error={stockLevels.error}
          fetchStockLevels={handleFetchStockLevels}
          locationId={selectedLocationId}
          locationName={selectedLocationName}
          autoRefresh={true}
          isAdmin={isAdmin}
        />
      ) : (
        <div className="card">
          <div className="shipment-list--empty">
            <div className="fa fa-map-marker icon" aria-hidden="true"></div>
            <div>Please select a location to view stock levels</div>
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  stockLevels: state.marketInventory.stockLevels,
  locations: state.marketInventory.locations
});

const mapDispatchToProps = {
  fetchLocationsSuccess,
  fetchLocationsFailure,
  fetchStockLevelsSuccess,
  fetchStockLevelsFailure
};

export default connect(mapStateToProps, mapDispatchToProps)(StockLevelsDashboardContainer);